import { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/quotationApi";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export default function RoomImages({ images = [], onChange, testIdPrefix = "room-image" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onPick = () => inputRef.current?.click();

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        const res = await api.post("/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({
          id: uid(),
          storage_path: res.data.storage_path,
          filename: res.data.filename,
          content_type: res.data.content_type,
        });
      }
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (id) => {
    onChange(images.filter((im) => im.id !== id));
  };

  return (
    <div data-testid={testIdPrefix}>
      <div className="flex items-center gap-2 flex-wrap">
        {images.map((im) => (
          <div
            key={im.id}
            className="relative w-20 h-20 rounded-lg overflow-hidden border border-white/10 group"
            data-testid={`${testIdPrefix}-${im.id}`}
          >
            <img
              src={fileUrl(im.storage_path)}
              alt={im.filename}
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => remove(im.id)}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              data-testid={`${testIdPrefix}-remove-${im.id}`}
              aria-label="Remove"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          className="w-20 h-20 rounded-lg border border-dashed border-white/15 flex flex-col items-center justify-center gap-1 text-white/50 hover:border-[#C5A880]/50 hover:text-[#C5A880] transition disabled:opacity-50"
          data-testid={`${testIdPrefix}-add`}
        >
          {uploading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              <Camera size={18} />
              <span className="text-[10px] uppercase tracking-wider">Add</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFileChange}
      />
    </div>
  );
}
